<script setup lang="ts">
/**
 * Список задач проекта — макет «11 · Навигация по задачам v2», фрейм 345-3:
 * таблица со строками 40, серверными секциями и табом «Показ».
 *
 * Второй вид того же проекта: доска отвечает на «что в работе», список — на
 * «покажи всё разом и дай отсортировать». Отбор живёт в URL и общий с доской
 * (DEV-2040) — переключение вида не теряет отобранное.
 *
 * Секции считает сервер (DEV-2043): клиентская группировка страницы из 25
 * строк рвала группы между страницами и врала в счётчиках.
 */
import { AppButton, AppModal, useToast } from '@vinprop/design-system'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ListTodo,
  SearchX,
} from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import InlinePicker from '@/components/InlinePicker.vue'
import IssueFilterBar from '@/components/IssueFilterBar.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ViewSwitch from '@/components/ViewSwitch.vue'
import ContextMenu from '@/components/ContextMenu.vue'
import UndoToast from '@/components/UndoToast.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { useAuthStore } from '@/features/auth/store'
import { setWatcher } from '@/features/issue/api'
import IssuePeek from '@/features/issue/components/IssuePeek.vue'
import BulkActionsBar from '@/features/issues/components/BulkActionsBar.vue'
import CreateIssuePanel from '@/features/issues/components/CreateIssuePanel.vue'
import IssueTableRow from '@/features/issues/components/IssueTableRow.vue'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { emptyFilters } from '@/features/issues/filters'
import { formatDueDate } from '@/features/issues/format'
import {
  dueFromChoice,
  isPriorityChoice,
  issueMenuItems,
  type DueChoice,
} from '@/features/issues/menu'
import { GROUP_OPTIONS, useIssueListState, type GroupBy } from '@/features/issues/listState'
import { useIssuesStore } from '@/features/issues/store'
import { listMembers, type ProjectMember } from '@/features/projects/api'

import type { IssueRow, IssueSortField, ServerGroup, ShowTab } from '@/features/issues/api'
import type { CreatedIssue } from '@/features/issues/api'
import { notifyIssueCreated } from '@/features/issues/created'

/** Сколько номеров страниц показывать вокруг текущей */
const PAGE_WINDOW = 5

const { t, locale } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const store = useIssuesStore()

// Отбор ⇄ адрес: URL всегда побеждает, peek (`?issue=`) список не трогает.
useIssueListState(store)

const projectKey = computed(() => String(route.params.key ?? ''))

const breadcrumbs = computed(() => [
  { label: t('nav.projects'), to: '/projects' },
  { label: store.project?.name || projectKey.value, to: `/p/${projectKey.value}/board` },
  { label: t('views.issues') },
])

const members = ref<ProjectMember[]>([])

const openList = async () => {
  await store.open(projectKey.value)

  // «Нет такого проекта» и «есть, но не для вас» — разные экраны, и различает
  // их только ответ сервера.
  const failureRoute = errorRoute(store.error, projectKey.value)

  if (failureRoute) {
    await router.replace(failureRoute)

    return
  }

  // Участники нужны только выпадашке исполнителя: отказ здесь оставляет её
  // пустой и не мешает списку.
  members.value = store.projectId ? await listMembers(store.projectId).catch(() => []) : []

  // Однократный хинт о новом дефолте: порядок списка сменился на «свежие
  // сверху», и без объяснения это читается как поломка привычного (DEV-2041).
  if (!store.sortHintSeen && !store.error) {
    toast.info(t('issues.list.sortHint'))
    store.markSortHintSeen()
  }
}

const groupOptions = computed(() =>
  GROUP_OPTIONS.map((value) => ({ value, label: t(`issues.list.groupBy.${value}`) })),
)

const groupCaption = computed(() =>
  store.grouping === 'none'
    ? t('issues.list.groupBy.label')
    : `${t('issues.list.groupBy.label')}: ${t(`issues.list.groupBy.${store.grouping}`)}`,
)

/** Таб «Показ»: открытые по умолчанию — закрытые больше не мешаются (DEV-2041) */
const tabOptions = computed(() =>
  (['open', 'done', 'all'] as ShowTab[]).map((value) => ({
    value,
    label: t(`issues.list.show.${value}`),
  })),
)

const tabCaption = computed(
  () => `${t('issues.list.show.label')}: ${t(`issues.list.show.${store.tab}`)}`,
)

/**
 * Подпись секции: у колонки — имя с сервера, у исполнителя — имя из строк,
 * у приоритета и типа — перевод по сырому ключу базы (HIGH, TASK).
 */
const groupName = (group: ServerGroup): string => {
  if (!group.key) {
    switch (store.grouping) {
      case 'status':
        return t('issues.list.noStatus')
      case 'assignee':
        return t('issues.list.noAssignee')
      case 'priority':
        return t('issues.list.noPriority')
      default:
        return t('issues.list.noType')
    }
  }

  switch (store.grouping) {
    case 'priority':
      return t(`issues.priorities.PRIORITY_${group.key}`)
    case 'type':
      return t(`issues.types.ISSUE_TYPE_${group.key}`)
    default:
      return group.title || group.key
  }
}

/** Заголовки в порядке колонок; пустой ключ — колонка, по которой не сортируют */
const columns = computed((): { key: IssueSortField | ''; label: string }[] => [
  { key: '', label: 'type' },
  { key: 'key', label: 'key' },
  { key: '', label: 'title' },
  { key: '', label: 'status' },
  { key: 'priority', label: 'priority' },
  { key: '', label: 'assignee' },
  { key: 'due_date', label: 'due' },
  // Колонка дефолтной сортировки: без неё «свежие сверху» невидимы (DEV-2041).
  store.tab === 'done'
    ? { key: 'completed_at', label: 'closed' }
    : { key: 'updated_at', label: 'updated' },
])

/** Что показывает последняя колонка строки */
const stampField = computed<'updatedAt' | 'completedAt'>(() =>
  store.tab === 'done' ? 'completedAt' : 'updatedAt',
)

const onSort = (field: IssueSortField | '') => {
  if (field) void store.applySort(field)
}

/** Номера страниц: окно вокруг текущей — на сорока страницах все не нужны */
const pageNumbers = computed(() => {
  const last = store.pageCount
  const half = Math.floor(PAGE_WINDOW / 2)
  const start = Math.max(1, Math.min(store.page - half, last - PAGE_WINDOW + 1))

  return Array.from({ length: Math.min(PAGE_WINDOW, last) }, (_, index) => start + index)
})

const onMove = async (id: string, columnId: string) => {
  try {
    await store.moveTo(id, columnId)
  } catch (failure) {
    toast.error(errorMessage(failure, t('board.moveFailed')))
  }
}

const onAssign = async (
  id: string,
  assigneeId: string,
  assigneeName: string,
  assigneeAvatar: string,
) => {
  try {
    await store.assignTo(id, assigneeId, assigneeName, assigneeAvatar)
  } catch (failure) {
    toast.error(errorMessage(failure, t('issues.list.assignFailed')))
  }
}

/** Последняя отметка — начало Shift-диапазона */
let lastToggledId = ''

const onToggle = (id: string, shift: boolean) => {
  if (shift && lastToggledId) store.selectRange(lastToggledId, id)
  else store.toggleSelected(id)

  lastToggledId = id
}

/** Массовые действия — один вызов BulkUpdateIssues на пачку (DEV-2054) */
const isBulkBusy = ref(false)
/** Confirm закрытия: пачка выбрасывается из планов, молча нельзя */
const isCloseAsking = ref(false)

const runBulk = async (
  action: () => Promise<{ wipExceeded: boolean }>,
  failText: string,
) => {
  isBulkBusy.value = true

  try {
    const { wipExceeded } = await action()

    // Лимит мягкий, как у переноса с доски: перелив показывается, не запрещается.
    if (wipExceeded) toast.warning(t('issues.list.bulk.wipExceeded'))
  } catch (failure) {
    toast.error(errorMessage(failure, failText))
  } finally {
    isBulkBusy.value = false
  }
}

const onBulkMove = (columnId: string) =>
  runBulk(() => store.moveSelected(columnId), t('board.moveFailed'))

const onBulkAssign = (assigneeId: string) =>
  runBulk(() => store.assignSelected(assigneeId), t('issues.list.assignFailed'))

const onBulkPrioritize = (priority: import('@/api/clients/tracker-issue').V1Priority) =>
  runBulk(() => store.prioritizeSelected(priority), t('issue.saveFailed'))

const bulkCount = computed(() => store.selected.size)

const onBulkClose = async () => {
  isCloseAsking.value = false

  await runBulk(() => store.closeSelected(), t('issue.saveFailed'))
}

const onLoadMore = async (key: string) => {
  try {
    await store.loadMoreGroup(key)
  } catch (failure) {
    toast.error(errorMessage(failure, t('issues.list.error.title')))
  }
}

const isCreateOpen = ref(false)

const onCreated = async (issue: CreatedIssue) => {
  notifyIssueCreated(issue.key)

  await store.load()
}

onMounted(openList)

// Переход между проектами из сайдбара меняет только параметр маршрута — экран
// при этом не пересоздаётся.
watch(projectKey, openList)

/** Отказ правки из панели показываем тем же тостом, что и остальные */
const onPeekFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('issue.saveFailed')))
}

/** Подсказка «Перейти к DEV-57» из поля отбора (DEV-2042) */
const onOpenIssue = (key: string) => void router.push(`/issue/${key}`)

// ─────────────────────── контекст-меню строки (DEV-2051/2052) ───────────────────────

const auth = useAuthStore()
const menuRef = ref<InstanceType<typeof ContextMenu> | null>(null)
const menuTarget = ref<IssueRow | null>(null)

const menuItems = computed(() => issueMenuItems(t, store.columns))

const onRowMenu = (issue: IssueRow, x: number, y: number) => {
  menuTarget.value = issue
  menuRef.value?.open(x, y)
}

/** Отмена закрытия: куда вернуть и что сказать */
const undo = ref({ message: '', issueId: '', columnId: '' })

/** Инлайн-пикеры строки (DEV-2053): оптимистично, с откатом при отказе */
const onPrioritize = async (id: string, priority: import('@/api/clients/tracker-issue').V1Priority) => {
  try {
    await store.prioritize(id, priority)
  } catch (failure) {
    toast.error(errorMessage(failure, t('issue.saveFailed')))
  }
}

const onDue = async (id: string, dueDate: string) => {
  try {
    await store.setDue(id, dueDate)
  } catch (failure) {
    toast.error(errorMessage(failure, t('issue.saveFailed')))
  }
}

const onUndoClose = async () => {
  if (!undo.value.issueId || !undo.value.columnId) return

  try {
    await store.moveTo(undo.value.issueId, undo.value.columnId)
  } catch (failure) {
    toast.error(errorMessage(failure, t('board.moveFailed')))
  }
}

const onMenuSelect = async (key: string, child?: string) => {
  const issue = menuTarget.value

  if (!issue) return

  try {
    switch (key) {
      case 'self': {
        const me = auth.user

        if (me) await store.assignTo(issue.id, me.id, me.name)

        break
      }
      case 'status':
        if (child) await store.moveTo(issue.id, child)

        break
      case 'priority':
        if (child && isPriorityChoice(child)) await store.prioritize(issue.id, child)

        break
      case 'due':
        if (child) await store.setDue(issue.id, dueFromChoice(child as DueChoice))

        break
      case 'close': {
        const previous = await store.closeRow(issue.id)

        // Закрытие выбрасывает задачу из планов — молчать нельзя (DEV-2052).
        undo.value = {
          message: t('menu.closed', { key: issue.key }),
          issueId: issue.id,
          columnId: previous,
        }

        break
      }
      case 'reject': {
        const previous = await store.rejectRow(issue.id)

        undo.value = {
          message: t('menu.rejected', { key: issue.key }),
          issueId: issue.id,
          columnId: previous,
        }

        break
      }
      case 'watch':
        await setWatcher(issue.id, true, auth.user?.id)
        toast.success(t('menu.watching', { key: issue.key }))

        break
      case 'copy-key':
        await navigator.clipboard.writeText(issue.key)

        break
      case 'copy-link':
        await navigator.clipboard.writeText(`${window.location.origin}/issue/${issue.key}`)

        break
      case 'open-tab':
        window.open(`/issue/${issue.key}`, '_blank')

        break
    }
  } catch (failure) {
    toast.error(errorMessage(failure, t('issue.saveFailed')))
  }
}

// ─────────────────────────── мобильный режим ───────────────────────────

/**
 * На телефоне таблица нечитаема: строки становятся карточками по образцу
 * «Моих задач», отбор — скролл-чипами, сортировка и группа — шторкой
 * (DEV-2044). Bulk, Shift и инлайн-пикеры — desktop-only: попадать в них
 * пальцем нельзя, а длинный тап занят системным меню.
 */
const isMobile = useIsMobile()

const isSheetOpen = ref(false)

/** Поля сортировки в шторке — те же, что в заголовках таблицы */
const sheetSortFields = computed(() =>
  (['updated_at', 'completed_at', 'due_date', 'priority', 'key'] as IssueSortField[]).map(
    (field) => ({ field, label: t(`issues.list.columns.${SORT_LABELS[field]}`) }),
  ),
)

const SORT_LABELS: Record<IssueSortField, string> = {
  key: 'key',
  priority: 'priority',
  due_date: 'due',
  created_at: 'updated',
  updated_at: 'updated',
  completed_at: 'closed',
}

/** Просрочка карточки — как в строке таблицы: только у незакрытой задачи */
const isOverdueRow = (issue: { dueDate: string; columnId: string }): boolean => {
  if (!issue.dueDate) return false
  if (store.columnOf(issue.columnId)?.statusCategory === 'STATUS_CATEGORY_DONE') return false

  return new Date(issue.dueDate).getTime() < Date.now()
}

const dueLabel = (iso: string) => formatDueDate(iso, locale.value)
</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template v-if="store.project">
      <ViewSwitch :project-key="store.project.key" />

      <!-- В 390 пикселей фильтр-бар и чипы не встают: на телефоне отбор
           живёт скролл-чипами над списком, а сортировка — шторкой (DEV-2044). -->
      <template v-if="!isMobile">
        <IssueFilterBar
          :filters="store.filters"
          :project-id="store.project.id"
          :project-key="store.project.key"
          :type-profile="store.project.typeProfile"
          :columns="store.columns"
          @change="store.applyFilters"
          @open="onOpenIssue"
        />

        <InlinePicker
          :options="tabOptions"
          :model-value="store.tab"
          @update:model-value="store.setTab($event as ShowTab)"
        >
          <template #trigger="{ isOpen, toggle }">
            <button type="button" class="gchip" :aria-expanded="isOpen" @click="toggle()">
              {{ tabCaption }}
              <ChevronDown :size="14" />
            </button>
          </template>
        </InlinePicker>

        <InlinePicker
          :options="groupOptions"
          :model-value="store.grouping"
          @update:model-value="store.setGrouping($event as GroupBy)"
        >
          <template #trigger="{ isOpen, toggle }">
            <button type="button" class="gchip" :aria-expanded="isOpen" @click="toggle()">
              {{ groupCaption }}
              <ChevronDown :size="14" />
            </button>
          </template>
        </InlinePicker>
      </template>

      <AppButton variant="primary" size="small" @click="isCreateOpen = true">
        {{ t('toolbar.create') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <!-- Мобильная полоса отбора: табы скроллом + вход в шторку сортировки -->
  <div v-if="isMobile && store.project" class="mbar">
    <button
      v-for="option in tabOptions"
      :key="option.value"
      type="button"
      class="mbar__chip"
      :class="{ 'mbar__chip--active': store.tab === option.value }"
      @click="store.setTab(option.value)"
    >
      {{ option.label }}
    </button>

    <button type="button" class="mbar__chip mbar__chip--sheet" @click="isSheetOpen = true">
      {{ t('issues.list.mobile.arrange') }}
    </button>
  </div>

  <div class="ilist">
    <div v-if="store.isLoading && !store.visibleIssues.length" class="itable">
      <div v-for="index in 8" :key="index" class="itable__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="store.error"
      :icon="AlertTriangle"
      :title="t('issues.list.error.title')"
      :description="t('issues.list.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="store.load">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="store.isEmpty && store.isFiltered"
      :icon="SearchX"
      :title="t('board.nothingFound.title')"
      :description="t('board.nothingFound.description')"
    >
      <AppButton variant="secondary" @click="store.applyFilters(emptyFilters())">
        {{ t('toolbar.filters.reset') }}
      </AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="store.isEmpty"
      :icon="ListTodo"
      :title="t('issues.list.empty.title')"
      :description="t('issues.list.empty.description')"
    >
      <AppButton variant="primary" @click="isCreateOpen = true">
        {{ t('issues.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <template v-else>
      <!-- Панель действий появляется над таблицей, как только что-то отмечено:
           в макете чекбоксы есть, а действий над ними нет (DEV-1501). -->
      <BulkActionsBar
        v-if="store.selected.size"
        class="issues__bulk"
        :count="store.selected.size"
        :columns="store.columns"
        :members="members"
        :busy="isBulkBusy"
        @move="onBulkMove"
        @assign="onBulkAssign"
        @prioritize="onBulkPrioritize"
        @close="isCloseAsking = true"
        @clear="store.clearSelection()"
      />

      <!-- Мобильный карточный режим (DEV-2044): строки таблицы в 390 пикселей
           нечитаемы, карточка ведёт на страницу задачи — peek десктопный. -->
      <div v-if="isMobile" class="mlist">
        <template v-if="store.isSectioned">
          <template v-for="group in store.groups" :key="group.key">
            <button
              type="button"
              class="mlist__group"
              :aria-expanded="!store.collapsed.has(group.key)"
              @click="store.toggleCollapsed(group.key)"
            >
              <ChevronDown v-if="!store.collapsed.has(group.key)" :size="14" />
              <ChevronRight v-else :size="14" />
              <span class="mlist__group-name">{{ groupName(group) }}</span>
              <span class="mlist__group-count">{{ group.total }}</span>
            </button>

            <template v-if="!store.collapsed.has(group.key)">
              <RouterLink
                v-for="issue in group.issues"
                :key="issue.id"
                class="mcard"
                :to="`/issue/${issue.key}`"
                @contextmenu.prevent="onRowMenu(issue, $event.clientX, $event.clientY)"
              >
                <IssueTypeIcon :type="issue.type" />
                <span class="mcard__key">{{ issue.key }}</span>
                <span class="mcard__title">{{ issue.title }}</span>
                <span class="mcard__due" :class="{ 'mcard__due--overdue': isOverdueRow(issue) }">
                  {{ dueLabel(issue.dueDate) }}
                </span>
              </RouterLink>

              <button
                v-if="group.issues.length < group.total"
                type="button"
                class="mlist__more"
                @click="onLoadMore(group.key)"
              >
                {{ t('issues.list.showMore', { count: group.total - group.issues.length }) }}
              </button>
            </template>
          </template>
        </template>

        <RouterLink
          v-for="issue in store.isSectioned ? [] : store.issues"
          :key="issue.id"
          class="mcard"
          :to="`/issue/${issue.key}`"
          @contextmenu.prevent="onRowMenu(issue, $event.clientX, $event.clientY)"
        >
          <IssueTypeIcon :type="issue.type" />
          <span class="mcard__key">{{ issue.key }}</span>
          <span class="mcard__title">{{ issue.title }}</span>
          <span class="mcard__due" :class="{ 'mcard__due--overdue': isOverdueRow(issue) }">
            {{ dueLabel(issue.dueDate) }}
          </span>
        </RouterLink>
      </div>

      <div v-else class="itable">
        <div class="itable__head">
          <input
            class="itable__check"
            type="checkbox"
            :checked="store.selected.size === store.visibleIssues.length"
            :aria-label="t('issues.list.selectAll')"
            @change="store.toggleAll"
          />

          <component
            :is="column.key ? 'button' : 'span'"
            v-for="column in columns"
            :key="column.label"
            :type="column.key ? 'button' : undefined"
            class="itable__h"
            :class="{ 'itable__h--sortable': column.key }"
            @click="onSort(column.key)"
          >
            {{ t(`issues.list.columns.${column.label}`) }}

            <template v-if="column.key">
              <ChevronDown v-if="store.sort.field === column.key && !store.sort.desc" :size="12" />
              <ChevronRight
                v-else-if="store.sort.field === column.key"
                class="itable__sort-up"
                :size="12"
              />
              <ChevronsUpDown v-else class="itable__sort-idle" :size="12" />
            </template>
          </component>

          <!-- Слот колонки «…»: у шапки под ним пусто. -->
          <span class="itable__h" />
        </div>

        <!-- Секционный режим: серверные группы с честными счётчиками -->
        <template v-if="store.isSectioned">
          <template v-for="group in store.groups" :key="group.key">
            <button
              type="button"
              class="itable__group"
              :aria-expanded="!store.collapsed.has(group.key)"
              @click="store.toggleCollapsed(group.key)"
            >
              <ChevronDown v-if="!store.collapsed.has(group.key)" :size="14" />
              <ChevronRight v-else :size="14" />
              <span class="itable__group-name">{{ groupName(group) }}</span>
              <span class="itable__group-count">{{ group.total }}</span>
            </button>

            <template v-if="!store.collapsed.has(group.key)">
              <IssueTableRow
                v-for="issue in group.issues"
                :key="issue.id"
                :issue="issue"
                :columns="store.columns"
                :members="members"
                :is-selected="store.selected.has(issue.id)"
                :stamp-field="stampField"
                :pending-regroup="store.pendingRegroup.has(issue.id)"
                @toggle="onToggle"
                @move="onMove"
                @assign="onAssign"
                @prioritize="onPrioritize"
                @due="onDue"
                @menu="onRowMenu"
              />

              <button
                v-if="group.issues.length < group.total"
                type="button"
                class="itable__more"
                @click="onLoadMore(group.key)"
              >
                {{ t('issues.list.showMore', { count: group.total - group.issues.length }) }}
              </button>
            </template>
          </template>
        </template>

        <!-- Плоский режим: прежние страницы по 25 -->
        <IssueTableRow
          v-for="issue in store.isSectioned ? [] : store.issues"
          :key="issue.id"
          :issue="issue"
          :columns="store.columns"
          :members="members"
          :is-selected="store.selected.has(issue.id)"
          :stamp-field="stampField"
          @toggle="onToggle"
          @move="onMove"
          @assign="onAssign"
          @prioritize="onPrioritize"
          @due="onDue"
          @menu="onRowMenu"
        />
      </div>

      <footer class="ifoot">
        <span class="ifoot__shown">
          {{
            store.selected.size
              ? t('issues.list.selected', { count: store.selected.size })
              : t('issues.list.shown', { shown: store.visibleIssues.length, total: store.total })
          }}
        </span>

        <button
          v-if="store.selected.size"
          type="button"
          class="ifoot__clear"
          @click="store.clearSelection()"
        >
          {{ t('issues.list.clearSelection') }}
        </button>

        <!-- Футер честности таба (DEV-2041): открытые открытыми, а закрытые
             не исчезли — вот они, счётчиком и в один клик. -->
        <button
          v-if="store.tab === 'open' && store.doneTotal !== null && !store.selected.size"
          type="button"
          class="ifoot__done"
          @click="store.setTab('done')"
        >
          {{ t('issues.list.doneFooter', { count: store.doneTotal }) }}
        </button>

        <nav
          v-if="!store.isSectioned && store.pageCount > 1"
          class="ifoot__pages"
          :aria-label="t('issues.list.pages')"
        >
          <button
            type="button"
            class="ifoot__page"
            :disabled="store.page === 1"
            :aria-label="t('issues.list.prev')"
            @click="store.setPage(store.page - 1)"
          >
            ‹
          </button>

          <button
            v-for="number in pageNumbers"
            :key="number"
            type="button"
            class="ifoot__page"
            :class="{ 'ifoot__page--active': number === store.page }"
            @click="store.setPage(number)"
          >
            {{ number }}
          </button>

          <button
            type="button"
            class="ifoot__page"
            :disabled="store.page === store.pageCount"
            :aria-label="t('issues.list.next')"
            @click="store.setPage(store.page + 1)"
          >
            ›
          </button>
        </nav>
      </footer>
    </template>
  </div>

  <!-- Шторка сортировки и группировки (DEV-2044, макет 359-2839): выпадашкам
       на телефоне не хватает места, а шторку закрывает большой палец. -->
  <Teleport to="body">
    <div v-if="isSheetOpen" class="msheet" @click.self="isSheetOpen = false">
      <div class="msheet__panel">
        <h3 class="msheet__caption">{{ t('issues.list.mobile.sort') }}</h3>

        <button
          v-for="option in sheetSortFields"
          :key="option.field"
          type="button"
          class="msheet__option"
          :class="{ 'msheet__option--active': store.sort.field === option.field }"
          @click="store.applySort(option.field)"
        >
          {{ option.label }}
          <span v-if="store.sort.field === option.field">{{ store.sort.desc ? '↓' : '↑' }}</span>
        </button>

        <h3 class="msheet__caption">{{ t('issues.list.groupBy.label') }}</h3>

        <button
          v-for="option in groupOptions"
          :key="option.value"
          type="button"
          class="msheet__option"
          :class="{ 'msheet__option--active': store.grouping === option.value }"
          @click="((isSheetOpen = false), store.setGrouping(option.value))"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
  </Teleport>

  <CreateIssuePanel
    v-if="store.project"
    v-model="isCreateOpen"
    :project-id="store.project.id"
    :type-profile="store.project.typeProfile"
    column-id=""
    @created="onCreated"
  />

  <!-- Confirm закрытия пачки (макет 356-2836): закрытые уходят из планов. -->
  <AppModal
    v-if="isCloseAsking"
    model-value
    :title="t('issues.list.bulk.closeTitle')"
    size="small"
    @update:model-value="isCloseAsking = false"
  >
    <p class="issues__ask">{{ t('issues.list.bulk.closeAsk', { count: bulkCount }) }}</p>

    <template #footer>
      <AppButton variant="secondary" @click="isCloseAsking = false">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton :loading="isBulkBusy" @click="onBulkClose">
        {{ t('issues.list.bulk.close') }}
      </AppButton>
    </template>
  </AppModal>

  <ContextMenu ref="menuRef" :items="menuItems" @select="onMenuSelect" />

  <UndoToast
    :message="undo.message"
    :action-label="t('menu.undo')"
    @action="onUndoClose"
  />

  <IssuePeek @failed="onPeekFailed" />
</template>

<style scoped>
.ilist {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  padding: 20px 24px;
  overflow-y: auto;
}

.issues__bulk {
  margin-bottom: 12px;
}

.issues__ask {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
}

.itable {
  /* Сетка из макета: отступы 16 по краям, шаг 12, гибкое только название.
     Последние 130 — колонка «Обновлена»: она показывает дефолтную сортировку,
     и место под неё зарезервировано макетом (DEV-2041). */
  --itable-columns: 40px 32px 88px minmax(0, 1fr) 120px 40px 48px 88px 130px 24px;

  display: flex;
  flex-direction: column;
  /* Таблица не ужимается под остаток экрана — она его перерастает, и лишнее
     уходит в прокрутку страницы. Без этого строки сплющиваются до 28. */
  flex-shrink: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: hidden;
}

.itable__head {
  display: grid;
  grid-template-columns: var(--itable-columns);
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  height: 36px;
  padding: 0 16px;
}

.itable__check {
  /* У флажка свой браузерный margin, и без сброса колонка съезжает на 4px
     вправо — заголовок с ней вместе, а строка ниже уже нет. */
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.itable__h {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.04em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.itable__h--sortable {
  cursor: pointer;
}

.itable__h--sortable:hover {
  color: var(--color-text-secondary);
}

/* Направление сортировки — тем же шевроном, повёрнутым вверх: две разные
   иконки на одном месте читаются как два разных элемента. */
.itable__sort-up {
  transform: rotate(-90deg);
}

.itable__sort-idle {
  opacity: 0.5;
}

.itable__group {
  /* Заголовок секции липнет к верху прокрутки: в длинной секции без него
     непонятно, чьи строки на экране. z-index ниже панелей и модалок ДС —
     их слои начинаются с 9999 (грабля слоёв наложений). */
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 16px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
  width: 100%;
}

.itable__group-name {
  font-weight: var(--font-weight-medium);
}

.itable__group-count {
  color: var(--color-text-muted);
}

.itable__more {
  flex-shrink: 0;
  height: 32px;
  padding: 0 16px 0 44px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.itable__more:hover {
  background: var(--color-bg-muted);
}

.itable__skeleton {
  flex-shrink: 0;
  height: 40px;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
}

.ifoot {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  height: 36px;
  margin-top: 4px;
  padding: 0 4px;
}

.ifoot__shown {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.ifoot__clear {
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.ifoot__done {
  border: none;
  background: none;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.ifoot__done:hover {
  color: var(--color-primary);
}

.ifoot__pages {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.ifoot__page {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.ifoot__page:hover:not(:disabled) {
  background: var(--color-bg-muted);
}

.ifoot__page:disabled {
  color: var(--color-text-muted);
  cursor: default;
  opacity: 0.5;
}

.ifoot__page--active {
  border-color: var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
}

/* Чипы «Показ» и группировки повторяют фильтр-чипы панели: они стоят в одном
   ряду, и разный рост или радиус между ними заметнее, чем кажется. */
.gchip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.gchip:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

/* ───────────────────────── мобильный режим (DEV-2044) ───────────────────── */

.mbar {
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
  /* Чипы уходят в горизонтальный скролл сами — страница вбок не ездит. */
  overflow-x: auto;
}

.mbar__chip {
  flex-shrink: 0;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}

.mbar__chip--active {
  border-color: var(--color-primary);
  background: var(--color-primary-tint);
  color: var(--color-text-primary);
}

.mbar__chip--sheet {
  margin-left: auto;
}

.mlist {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: hidden;
}

.mlist__group {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 0 16px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
}

.mlist__group-name {
  font-weight: var(--font-weight-medium);
}

.mlist__group-count {
  color: var(--color-text-muted);
}

.mlist__more {
  height: 36px;
  padding: 0 16px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
}

/* Карточка — образец .mrow из «Моих задач»: тот же рост и та же типографика,
   чтобы два мобильных списка не выглядели двумя приложениями. */
.mcard {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 16px;
  border-top: 1px solid var(--color-border);
  color: inherit;
  text-decoration: none;
}

.mcard__key {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}

.mcard__title {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mcard__due {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.mcard__due--overdue {
  color: var(--color-danger);
}

.msheet {
  position: fixed;
  inset: 0;
  /* Ниже слоёв ДС (их наложения начинаются с 9999), выше контента экрана. */
  z-index: 900;
  display: flex;
  align-items: flex-end;
  background: rgb(0 0 0 / 40%);
}

.msheet__panel {
  width: 100%;
  max-height: 70vh;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  background: var(--color-surface);
  overflow-y: auto;
}

.msheet__caption {
  margin: 12px 0 4px;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.msheet__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 44px;
  padding: 0 4px;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body);
  text-align: left;
}

.msheet__option--active {
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}
</style>
